import { Controller } from '@hotwired/stimulus'
import { LocalClient } from '../clients'
import { targetUpdate } from './components/component_controller'
import { DispatchEvent, SelectOption } from '../types'

interface OptionsFromApi {
  options: SelectOption[]
}

export class EditDeviceFormController extends Controller {
  static targets = [
    'deviceType',
    'serialNumber',
    'location',
    'site',
    'placement',
    'primaryContact',
    'form',
    'banner',
    'warrantyExpiresOnHelpText',
    'datePicker',
  ]

  static values = {
    warrantyExpiresOnHelpTextDefault: String,
  }

  declare readonly deviceTypeTarget: HTMLInputElement
  declare readonly serialNumberTarget: HTMLInputElement
  declare readonly locationTarget: HTMLSelectElement
  declare readonly siteTarget: HTMLSelectElement
  declare readonly placementTarget: HTMLInputElement
  declare readonly primaryContactTarget: HTMLSelectElement
  declare readonly formTarget: HTMLFormElement
  declare readonly bannerTarget: HTMLElement
  declare readonly warrantyExpiresOnHelpTextTarget: HTMLInputElement
  declare readonly datePickerTarget: HTMLElement

  declare readonly warrantyExpiresOnHelpTextDefaultValue: string

  declare locationId: string | null
  declare organizationId: string | null
  declare deviceTypeId: string | null
  declare formWasValid: boolean

  requiredFields = [
    'device[active_status]',
    'device[equipment_type_id]',
    'device[location_id]',
    'device[organization_id]',
    'device[placement]',
    'device[primary_point_of_contact_id]',
    'device[serial_number]',
    'device[site_id]',
  ]

  connect() {
    super.connect()
    this.formWasValid = this.checkFormValidity()
  }

  checkFormValidity() {
    const formData = new FormData(this.formTarget)
    const formObject = Object.fromEntries(formData)
    const isFormValid = this.requiredFields.every((field) => formObject[field])

    return isFormValid
  }

  toggleCompletedBanner() {
    const isFormValid = this.checkFormValidity()
    if (this.formWasValid && !isFormValid) {
      this.bannerTarget.classList.add('hidden')
    }
    if (!this.formWasValid && isFormValid) {
      this.bannerTarget.classList.remove('hidden')
    }
    this.formWasValid = isFormValid
  }

  public async handleOrganizationSelect(event: DispatchEvent): Promise<void> {
    const organizationId = event.detail.value
    if (organizationId != this.organizationId) {
      this.organizationId = organizationId
      this.locationId = null

      const [locations, contacts] = await Promise.all([
        this.getOrganizationLocations(),
        this.getOrganizationContacts(),
      ])

      this.enableUserFields()
      this.setLocationSelectOptions(locations.options)
      this.setPrimaryContactOptions(contacts.options)
      this.disableSiteSelect()
      this.disablePlacementField()
    }
  }

  public async handleLocationSelect(event: DispatchEvent): Promise<void> {
    const locationId = event.detail.value
    if (locationId != this.locationId) {
      this.locationId = locationId
      this.disableSiteSelect()
      this.disablePlacementField()

      if (!locationId) {
        this.updateTimeZone('UTC')
        return
      }

      try {
        const [sites, location] = await Promise.all([
          this.getLocationSites(),
          this.getLocation(locationId),
        ])

        this.setSiteSelectOptions(sites.options)
        this.disablePlacementField()
        this.updateTimeZone(location.physical_address?.time_zone || 'UTC')
      } catch (error) {
        this.updateTimeZone('UTC')
      }
    }
  }

  private async getLocation(locationId: string) {
    return fetch(`/locations/${locationId}.json`).then((response) =>
      response.json(),
    )
  }

  private updateTimeZone(timeZone: string) {
    targetUpdate(this.datePickerTarget, {
      time_zone: timeZone,
    })
  }

  public async handleSiteSelect(event: DispatchEvent): Promise<void> {
    const siteId = event.detail.value
    this.enablePlacementField()
  }

  public handleEquipmentTypeSelect(event: DispatchEvent): void {
    const deviceTypeId = event.detail.value
    if (deviceTypeId != this.deviceTypeId) {
      this.deviceTypeId = deviceTypeId
      if (this.deviceTypeId) {
        fetch(`/device_types/${this.deviceTypeId}`)
          .then((response) => response.json())
          .then((data) => {
            const {
              device_type: { warranty_expires_on_help_text },
            } = data
            this.warrantyExpiresOnHelpTextTarget.innerText =
              warranty_expires_on_help_text
          })
      } else {
        this.warrantyExpiresOnHelpTextTarget.innerText =
          this.warrantyExpiresOnHelpTextDefaultValue
      }
    }
  }

  public clearForm() {
    this.disableLocationSelect()
    this.disableSiteSelect()
    this.disablePlacementField()
  }

  // form state

  private disableLocationSelect(): void {
    targetUpdate(this.locationTarget, {
      options: [],
      disabled: true,
      value: null,
    })
  }

  private setLocationSelectOptions(options: SelectOption[]): void {
    targetUpdate(this.locationTarget, {
      options: options,
      disabled: false,
      value: null,
    })
  }

  private disableSiteSelect(): void {
    targetUpdate(this.siteTarget, { options: [], disabled: true, value: null })
  }

  private setSiteSelectOptions(options: SelectOption[]): void {
    targetUpdate(this.siteTarget, {
      options: options,
      disabled: false,
      value: null,
    })
  }

  private disablePlacementField(): void {
    this.placementTarget.value = ''
    this.placementTarget.setAttribute('disabled', 'true')
    this.placementTarget.setAttribute('readonly', 'true')
  }
  private enablePlacementField(): void {
    this.placementTarget.removeAttribute('disabled')
    this.placementTarget.removeAttribute('readonly')
  }

  private enableUserFields(): void {
    this.primaryContactTarget.removeAttribute('disabled')
  }

  // network

  private async getOrganizationLocations(): Promise<OptionsFromApi> {
    if (!this.organizationId) {
      throw new Error('need an organization id to load locations')
    }

    const path = '/organization_locations'
    const params = `organization_id=${this.organizationId}`
    return await LocalClient.queryPath({ path, params })
  }

  private async getLocationSites(): Promise<OptionsFromApi> {
    if (!this.locationId) {
      throw new Error('need a location id to load sites')
    }

    const path = `/locations/sites`
    const params = `location_ids=${this.locationId}`
    return await LocalClient.queryPath({ path, params })
  }

  private async getOrganizationContacts(): Promise<OptionsFromApi> {
    if (!this.organizationId) {
      return { options: [] }
    }

    const path = '/organization_contacts'
    const params = `organization_id=${this.organizationId}`
    return await LocalClient.queryPath({ path, params })
  }

  private setPrimaryContactOptions(options: SelectOption[]): void {
    targetUpdate(this.primaryContactTarget, {
      options,
      disabled: false,
      value: null,
    })
  }
}
